import { useEffect } from 'react';

// נגישות לחלונות קופצים (WCAG 2.4.3 סדר מיקוד, 2.1.2 בלי מלכודת מקלדת): לכל אלמנט role="dialog" aria-modal="true" באתר -
//   • כשנפתח: הפוקוס עובר לתוכו (לשדה עם autoFocus אם יש, אחרת לרכיב הראשון);
//   • Tab / Shift+Tab מסתובבים בתוך החלון ולא בורחים לעמוד שמאחוריו;
//   • כשנסגר: הפוקוס חוזר לכפתור שפתח אותו.
// מופעל פעם אחת ב-ZolBakfarApp, ותופס גם חלונות חדשים - כל חלון חדש צריך רק role="dialog" ו-aria-modal="true".

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

const openDialogs = () => [...document.querySelectorAll('[role="dialog"][aria-modal="true"]')];
const focusables = (dialog) => [...dialog.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);

export const useModalFocus = () => {
  useEffect(() => {
    const returnTo = new Map();   // חלון פתוח → האלמנט שהיה בפוקוס לפני שנפתח

    const onOpened = (dialog) => {
      returnTo.set(dialog, document.activeElement);
      // אחרי הרינדור - autoFocus של React כבר רץ
      requestAnimationFrame(() => {
        if (dialog.contains(document.activeElement)) return;
        const first = focusables(dialog)[0];
        if (first) first.focus();
        else { dialog.setAttribute('tabindex', '-1'); dialog.focus(); }
      });
    };

    const sync = () => {
      const current = new Set(openDialogs());
      current.forEach((d) => { if (!returnTo.has(d)) onOpened(d); });
      [...returnTo.keys()].forEach((d) => {
        if (current.has(d)) return;
        const el = returnTo.get(d);
        returnTo.delete(d);
        if (el && document.contains(el) && typeof el.focus === 'function') el.focus();
      });
    };

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { childList: true, subtree: true });
    sync();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const dialogs = openDialogs();
      const dialog = dialogs[dialogs.length - 1];   // העליון
      if (!dialog) return;
      const items = focusables(dialog);
      if (!items.length) { e.preventDefault(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (!dialog.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);
};
